﻿import { createRouter, createWebHistory } from 'vue-router'
import type { RouteLocationNormalized, RouteLocationRaw, RouteRecordRaw } from 'vue-router'
import { useAuth } from '@/composables/useAuth'

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
  }
}

export const Routes = {
  login: 'login',
  start: 'start',
  operator: 'operator',
  customers: 'customers',
  customerNew: 'customer-new',
  customerCoreData: 'customer-core-data',
  customerPos: 'customer-pos',
  customerDevices: 'customer-devices',
  customerDeviceDetail: 'customer-device-detail',
  customerApiKeys: 'customer-api-keys',
  customerAuthCards: 'customer-auth-cards',
  customerReports: 'customer-reports',
  issuers: 'issuers',
  issuerNew: 'issuer-new',
  issuerCoreData: 'issuer-core-data',
  issuerCards: 'issuer-cards',
  issuerWebcharges: 'issuer-webcharges',
  issuerReports: 'issuer-reports',
  issuerUsers: 'issuer-users',
  sepaCustomer: 'sepa-customer',
  sepaIssuer: 'sepa-issuer',
  reports: 'reports',
  merchantTransactionDetail: 'merchant-transaction-detail',
  merchantTransactions: 'merchant-transactions',
  transactionDetail: 'transaction-detail',
  transactions: 'transactions',
  issuerTransactionDetail: 'issuer-transaction-detail',
  issuerTransactions: 'issuer-transactions',
  adminUsers: 'admin-users',
  adminCards: 'admin-cards',
  adminImports: 'admin-imports',
  adminDeviceNew: 'admin-device-new',
  adminDeviceDetail: 'admin-device-detail',
  adminDevices: 'admin-devices',
  adminAccountGroupNew: 'admin-account-group-new',
  adminAccountGroupDetail: 'admin-account-group-detail',
  adminAccountGroups: 'admin-account-groups',
  adminAccountDetail: 'admin-account-detail',
  adminAccounts: 'admin-accounts',
  adminPasswordResets: 'admin-password-resets',
  settingsGeneral: 'settings-general',
  settingsIssuer: 'settings-issuer',
  userChangePassword: 'user-change-password',
  cardUsers: 'card-users',
  cardUserCoreData: 'card-user-core-data',
  schedulerJobs: 'scheduler-jobs',
  dailyTotals: 'daily-totals',
} as const

export type RouteName = typeof Routes[keyof typeof Routes]

type TypedRoute = Omit<RouteRecordRaw, 'name'> & { name?: RouteName }

const routes = [
  {
    path: '/login',
    name: Routes.login,
    component: () => import('@/components/authentication/LoginView.vue'),
  },
  {
    path: '/',
    name: Routes.start,
    component: () => import('@/components/Start.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/operator',
    name: Routes.operator,
    component: () => import('@/components/operator/OperatorManagement.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/customers',
    name: Routes.customers,
    component: () => import('@/components/customers/CustomerManagement.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/customers/new',
    name: Routes.customerNew,
    component: () => import('@/components/customers/CustomerCoreData.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/customers/:id/details',
    name: Routes.customerCoreData,
    component: () => import('@/components/customers/CustomerCoreData.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/customers/:id/pos',
    name: Routes.customerPos,
    component: () => import('@/components/customers/CustomerPointOfSalesManagement.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/customers/:id/devices',
    name: Routes.customerDevices,
    component: () => import('@/components/customers/CustomerDeviceManagement.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/customers/:id/devices/:deviceId',
    name: Routes.customerDeviceDetail,
    component: () => import('@/components/customers/CustomerDeviceDetailPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/customers/:id/api-keys',
    name: Routes.customerApiKeys,
    component: () => import('@/components/customers/CustomerApiKeyManagement.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/customers/:id/auth-cards',
    name: Routes.customerAuthCards,
    component: () => import('@/components/customers/CustomerAuthCardManagement.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/customers/:id/reports',
    name: Routes.customerReports,
    component: () => import('@/components/customers/CustomerReports.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/issuers',
    name: Routes.issuers,
    component: () => import('@/components/issuers/IssuerManagement.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/issuers/new',
    name: Routes.issuerNew,
    component: () => import('@/components/issuers/IssuerCoreData.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/issuers/:id/details',
    name: Routes.issuerCoreData,
    component: () => import('@/components/issuers/IssuerCoreData.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/issuers/:id/cards',
    name: Routes.issuerCards,
    component: () => import('@/components/issuers/AdministerIssuerCards.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/issuers/:id/webcharges',
    name: Routes.issuerWebcharges,
    component: () => import('@/components/issuers/AdministerIssuerWebcharges.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/issuers/:id/reports',
    name: Routes.issuerReports,
    component: () => import('@/components/issuers/IssuerReports.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/issuers/:id/users',
    name: Routes.issuerUsers,
    component: () => import('@/components/issuers/IssuerUserManagement.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/sepa/customer-transfers',
    name: Routes.sepaCustomer,
    component: () => import('@/components/bankTransfers/CustomerBankTransfers.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/sepa/issuer-transfers',
    name: Routes.sepaIssuer,
    component: () => import('@/components/bankTransfers/IssuerBankTransfers.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reports',
    name: Routes.reports,
    component: () => import('@/components/reports/Report.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/analytics/transactions/merchants/:transactionNumber',
    name: Routes.merchantTransactionDetail,
    component: () => import('@/components/reports/MerchantTransactionDetailPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/analytics/transactions/merchants',
    name: Routes.merchantTransactions,
    component: () => import('@/components/reports/MerchantTransactionManagement.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/analytics/transactions/:transactionNumber',
    name: Routes.transactionDetail,
    component: () => import('@/components/reports/TransactionDetailPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/analytics/transactions',
    name: Routes.transactions,
    component: () => import('@/components/reports/TransactionManagement.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/analytics/transactions/issuers/:transactionNumber',
    name: Routes.issuerTransactionDetail,
    component: () => import('@/components/reports/IssuerTransactionDetailPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/analytics/transactions/issuers',
    name: Routes.issuerTransactions,
    component: () => import('@/components/reports/IssuerTransactionManagement.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/users',
    name: Routes.adminUsers,
    component: () => import('@/components/administrations/proficontrolUser/ProfiControlUserManagement.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/cards',
    name: Routes.adminCards,
    component: () => import('@/components/cards/AdministerCards.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/imports',
    name: Routes.adminImports,
    component: () => import('@/components/administrations/Import/AdminImports.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/devices/new',
    name: Routes.adminDeviceNew,
    component: () => import('@/components/devices/DeviceDetailPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/devices/:id',
    name: Routes.adminDeviceDetail,
    component: () => import('@/components/devices/DeviceDetailPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/devices',
    name: Routes.adminDevices,
    component: () => import('@/components/devices/DeviceManagement.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/account-groups/new',
    name: Routes.adminAccountGroupNew,
    component: () => import('@/components/accountGroups/AccountGroupDetailPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/account-groups/:id',
    name: Routes.adminAccountGroupDetail,
    component: () => import('@/components/accountGroups/AccountGroupDetailPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/account-groups',
    name: Routes.adminAccountGroups,
    component: () => import('@/components/accountGroups/AccountGroupManagement.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/accounts/:accountNumber',
    name: Routes.adminAccountDetail,
    component: () => import('@/components/accounts/AccountDetailPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/accounts',
    name: Routes.adminAccounts,
    component: () => import('@/components/accounts/AccountManagement.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/password-resets',
    name: Routes.adminPasswordResets,
    component: () => import('@/components/administrations/PasswordReset.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/settings/general',
    name: Routes.settingsGeneral,
    component: () => import('@/components/settings/GeneralSettings.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/settings/issuer',
    name: Routes.settingsIssuer,
    component: () => import('@/components/settings/IssuerSettings.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/user/change-password',
    name: Routes.userChangePassword,
    component: () => import('@/components/ChangePassword.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/card-users',
    name: Routes.cardUsers,
    component: () => import('@/components/cardUsers/CardUsers.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/card-users/:id/details',
    name: Routes.cardUserCoreData,
    component: () => import('@/components/cardUsers/CardUserCoreData.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/scheduler-jobs',
    name: Routes.schedulerJobs,
    component: () => import('@/components/schedulerJobs/SchedulerJobs.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/daily-totals',
    name: Routes.dailyTotals,
    component: () => import('@/components/reports/DailyTotalsManagement.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: Routes.start },
  },
] satisfies TypedRoute[]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

export function authGuard(to: RouteLocationNormalized): RouteLocationRaw | void {
  const { isAuthenticated, restoreFromStorage } = useAuth()
  restoreFromStorage()
  if (to.meta.requiresAuth && !isAuthenticated.value) {
    return { path: '/login', query: { redirectTo: to.fullPath } }
  }
  if (to.path === '/login' && isAuthenticated.value) {
    return { path: '/' }
  }
}

router.beforeEach(authGuard)

router.onError((error, to) => {
  if (
    error.message?.includes('Failed to fetch dynamically imported module') ||
    error.message?.includes('Importing a module script failed') ||
    error.name === 'ChunkLoadError'
  ) {
    window.location.href = to.fullPath
  }
})

export default router
